import * as moment from 'moment-business-days';
import { parseDomain } from 'helpers/parseDomain';

import {
  Inject,
  Injectable,
} from 'ng-metadata/core';
import { EtomsService } from './etoms.service';

@Injectable('ProductService')
export class ProductService {
  public laroute = (<any>window).laroute;
  public url: string = process.env.MIX_API;
  public portal: string;

  constructor(
    @Inject('$q') private $q: ng.IQService,
    @Inject('$http') private $http: ng.IHttpService,
    @Inject(EtomsService) protected etomsService: EtomsService,
  ) {
    const domain = parseDomain(window.location.href);
    this.portal = domain.domain ? domain.domain : 'travelvisa';
  }

  /**
   * Get single product by ID
   *
   * @param {string} productId Product's UUID
   * @param citizenship
   * @param residency
   */
  async getProduct(productId: string, citizenship: string, residency: string): Promise<any> {
    if (typeof productId !== 'string') {
      return this.handleError('`getProduct` method requires `productId` parameter');
    }

    try {
      const etoms = this.etomsService.enabled() ? 'etoms/' : '';
      const response: any = await this.getRequest(`/${etoms}product/${productId}/${citizenship}/${residency}/${this.portal}`, null);
      return response.data || [];
    } catch (e) {
      throw e;
    }
  }

  /**
   * Get list of products filtered by criteria
   *
   * @param  {string|(string)[]}    country Single or array of country codes
   * @param  {string}               type    Type of product (visa, passport)
   * @param  {string}               subtype Subtype of product
   * @param citizenship
   * @param residency
   * @return {ng.IHttpPromise<any>}
   */
  async getList(country?: string|string[], type?: string, subtype?: string, citizenship?: string, residency?: string) {
    if (type === 'visa' && this.etomsService.enabled()) {
      try {
        const response: any = await this.getRequest(`/etoms/product/country/${country}/details/simple/${citizenship}/${residency}/${this.portal}`, null);
        return response.data.products || [];
      } catch (e) {
        throw e;
      }
    } else {
      const data = {
        type,
        subtype,
        country,
      };

      try {
        const list: any = await this.postRequest('/product', data);
        return list.data;
      } catch (e) {
        throw e;
      }
    }
  }

  /**
   * Get filtered list of visas for citizenship/residency options
   *
   * @param {string} country
   * @param {string} citizenship
   * @param {string} residency
   * @returns {Promise<object[]>}
   * @memberof ProductService
   */
  async getFilteredList(
    country: string,
    citizenship: string,
    residency: string,
  ): Promise<object[]> {
    const list: any =
      await this.getRequest(`/product/filter/${country}/${citizenship}/${residency}`, {});
    return list;
  }

  async getRequirements(data) {
    const etoms = this.etomsService.enabled() ? 'etoms/' : '';
    const response: any = await this.postRequest(`/${etoms}product/requirements/${this.portal}`, data);
    return response.data || false;
  }

   /**
   * Return formatted array of product options
   *
   * @param {string} [deliveryDate]
   * @returns {Promise<any>}
   * @memberof ProductService
   */
  async getPassportProducts(): Promise<any> {
    try {
      const response: any = await this.getRequest(`/product/passports/simple/list`);

      return response.data.passports;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Fetch product options
   */
  async getPassportOptions() {
    try {
      const parsed = parseDomain(window.location.href);
      const response: any = await this.getRequest(`/domains/${parsed.domain}/options`);
      return this.mapPassportOptions(response.data);
    } catch (e) {
      throw e;
    }
  }

  /**
   * Return formatted array of product options
   *
   * @param {string} [deliveryDate]
   * @returns {Promise<any>}
   * @memberof ProductService
   */
  mapPassportOptions(processingOptions) {
    return {
      addons: processingOptions.addons.map((addon) => {
        addon.price = addon.price / 100;

        if (addon.label === 'Passport Card') {
          addon.url = this.laroute.route('passports.passport-card');
        }

        return addon;
      }),
      options: processingOptions.options.map((opt) => {
        return {
          uuid: opt.uuid,
          label: opt.friendly_name,
          recommended: opt.recommended,
          processingFee: opt.cost / 100,
          inboundShipping: opt.shipping.cost / 100,
          inboundShippingLabel: opt.shipping.friendly_name,
          daysRange: this.getDaysRange(opt.from, opt.to),
          renewal: opt.renewal,
          mailaway: opt.mailaway
          // deliveryDate: this.getDeliveryDate(deliveryDate, opt.to),
        };
      }),
    };
  }

  async getOutboundOptions(): Promise<any> {
    try {
      const etoms = this.etomsService.enabled() ? 'etoms/' : '';
      const response: any = await this.getRequest(`/${etoms}shipping/costs/${this.portal}`);
      const options = response.data || [];

      return this.mapOutboundOptions(options);
    } catch (e) {
      throw e;
    }
  }

  mapOutboundOptions(options: [any]) {
    return options.map((option) => {
      return {
        label: option.friendly_name,
        desc: option.description,
        value: option.speed,
        price: option.cost / 100,
        selectable: option.list,
      };
    });
  }

  /**
   * Confirms a traveler with citizenship/residency and
   * determins if visa is required or not.
   *
   * @param {string} productId
   * @param {string} citizenship
   * @param {string} residency
   * @returns {Promise<any>}
   * @memberof ProductService
   */
  async confirmProductRequirement(
    productId: string,
    citizenship: string,
    residency: string,
  ): Promise<any> {
    const data = {
      citizenship,
      residency,
    };

    const etoms = this.etomsService.enabled() ? 'etoms/' : '';
    return this.postRequest(`/${etoms}product/${productId}/traveler/options/${this.portal}`, data);
  }

  /**
   * Returns hypen delimited estimation of days
   * from a processing time duration.
   * Range of days is hardcoded.
   *
   * @param   {string} processingTime
   * @returns {string}
   */
  getDaysRange(rangeFrom: string, rangeTo: string): string {
    const durationFrom = moment.duration(rangeFrom);
    const daysMaxFrom = durationFrom.asDays();

    const durationTo = moment.duration(rangeTo);
    const daysMaxTo = durationTo.asDays();

    if (daysMaxFrom === daysMaxTo) {
      return `${daysMaxFrom}`;
    }

    return `${daysMaxFrom}-${daysMaxTo}`;
  }

  /**
   * @param  {string} departureDate  Departure date ISO-8601
   * @param  {string} processingTime ISO-8601 duration
   * @return {string}                ISO-8601 date of delivery
   */
  getDeliveryDate(departureDate: string, processingTime: string): string {
    const departure = moment(departureDate);
    const duration = moment.duration(processingTime);

    if (! departure.isValid()) return;
    if (! moment.isDuration(duration)) return;

    return departure.businessAdd(duration.asDays()).format();
  }


  async getRequest(resourcePath, params?): Promise<any> {
    const config = {
      params,
      method: 'GET',
      url: this.url + resourcePath,
    };

    try {
      const response = await this.$http(config);
      return response.data;
    } catch (e) {
      return this.$q.reject(e);
    }
  }

  postRequest(resourcePath, data?) {
    const config = {
      data,
      method: 'POST',
      url: this.url + resourcePath,
    };

    return this.$http(config)
      .then(this.handleSuccess.bind(this))
      .catch(this.handleError.bind(this))
    ;
  }

  handleSuccess(response) {
    if (response.data) {
      return response.data;
    }

    return false;
  }

  handleError(response) {
    return this.$q.reject(response);
  }

}



// WEBPACK FOOTER //
// ./resources/assets/js/services/product.service.ts